import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../services/api";
import { withRetry } from "./useCreditosData";

/**
 * Todos os itens de um fornecedor, somando os seus tipos de crédito.
 * Hoje a API só entrega itens por tipo (/creditos/modal/:tipo?fornecedor=),
 * ordenados por data, em páginas de 200 e com teto de 10.000 itens por tipo.
 * A carga começa quando `enabled` fica verdadeiro e segue em segundo plano até
 * a última página: uma chamada por vez, com pausa, alternando entre os tipos.
 * O resultado completo fica em cache por 10 minutos.
 */
const PAGE_SIZE = 200;
const PAUSE_MS = 150;
const CACHE_TTL = 10 * 60 * 1000;
const cache = new Map();

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

const initialCursors = (tipos) => tipos
  .filter((t) => t.fornecedor?.codigo)
  .map((t) => ({ tipo: t, page: 0, totalPages: 1 }));

const cacheKey = (supplier) => `${localStorage.getItem("vidal_selected_project")}|${supplier.id}`;
const EMPTY = { items: [], metodologias: {}, started: false, done: false };

export default function useSupplierItems(supplier, enabled) {
  const [state, setState] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const runId = useRef(0);
  const cursors = useRef([]);
  const turn = useRef(0);

  const pending = () => cursors.current.filter((c) => c.page < c.totalPages);

  // Uma página do próximo tipo que ainda tem itens, em rodízio
  const loadPage = useCallback(async (id) => {
    const isStale = () => runId.current !== id;
    const open = pending();
    const cursor = open[turn.current % open.length];
    turn.current += 1;
    const nextPage = cursor.page + 1;
    const { credito, fornecedor } = cursor.tipo;
    const { data } = await withRetry(() => api.get(`/creditos/modal/${credito.tipoCredito}`, {
      params: { projeto: localStorage.getItem("vidal_selected_project"), page: nextPage, pageSize: PAGE_SIZE, fornecedor: fornecedor.codigo },
    }), isStale);
    if (isStale()) return;
    if (!data.success) throw new Error("Resposta sem sucesso");
    const d = data.data;
    const items = d.itensIdentificados || [];
    cursor.page = nextPage;
    // Página vazia encerra o tipo: a API para em 10.000 itens mesmo que o total seja maior
    cursor.totalPages = items.length === 0 ? nextPage : d.paginacao?.totalPages || 1;
    const tagged = items.map((item) => ({ ...item, tipoCredito: credito.tipoCredito, credito }));
    setState((prev) => ({
      ...prev,
      started: true,
      items: prev.items.concat(tagged),
      metodologias: d.metodologia ? { ...prev.metodologias, [credito.tipoCredito]: d.metodologia } : prev.metodologias,
    }));
  }, []);

  const run = useCallback(async (reset) => {
    if (reset) {
      runId.current += 1;
      cursors.current = initialCursors(supplier.tipos || []);
      turn.current = 0;
      setState({ ...EMPTY, started: true });
    }
    const id = runId.current;
    setLoading(true);
    setError(false);
    try {
      while (pending().length > 0 && runId.current === id) {
        // eslint-disable-next-line no-await-in-loop
        await loadPage(id);
        // eslint-disable-next-line no-await-in-loop
        if (pending().length > 0) await sleep(PAUSE_MS);
      }
      if (runId.current !== id) return;
      setState((prev) => {
        const next = { ...prev, done: true };
        cache.set(cacheKey(supplier), { at: Date.now(), items: next.items, metodologias: next.metodologias });
        return next;
      });
    } catch (err) {
      if (runId.current !== id) return;
      console.error("Erro ao carregar os itens do fornecedor:", err);
      setError(true);
    } finally {
      if (runId.current === id) setLoading(false);
    }
  }, [supplier, loadPage]);

  // Troca de fornecedor: descarta a carga anterior e reaproveita o cache
  useEffect(() => {
    runId.current += 1;
    cursors.current = initialCursors(supplier.tipos || []);
    turn.current = 0;
    const hit = cache.get(cacheKey(supplier));
    if (hit && Date.now() - hit.at < CACHE_TTL) {
      setState({ items: hit.items, metodologias: hit.metodologias, started: true, done: true });
    } else {
      setState(EMPTY);
    }
    setLoading(false);
    setError(false);
  }, [supplier]);

  useEffect(() => {
    if (enabled && !state.started) run(true);
  }, [enabled, state.started, run]);

  useEffect(() => () => { runId.current += 1; }, []);

  return {
    items: state.items,
    metodologias: state.metodologias,
    started: state.started,
    done: state.done,
    loading,
    error,
    // Continua de onde parou depois de uma falha
    resume: () => run(false),
    reload: () => { cache.delete(cacheKey(supplier)); run(true); },
  };
}
